<template>
    <div :class="'product-sidebar product-sidebar-' + type + (open ? ' opened' : '')">

        <button class="close-product-info d-inline-block d-lg-none" @click="$emit('close')"></button>

        <!-- Sidebar type is just fabric -->
        <SelectedFabric v-if="type === 'fabric'"/>

        <!-- Product is selected-->
        <template v-else-if="product">

            <!-- Selected product is shown over selected fabric -->
            <template v-if="type === 'both-over'">
                <img v-if="overlay" v-lazy="overlay" class="overlay">
                <img v-if="product.mainImage"
                     v-lazy="product.mainImage"
                     :alt="product.name"
                     :title="product.name"
                     :class="[{'under-overlay' : overlay}, {'thread' : productId == 'button-holes'}]"
                >
                <div v-else class="image-substitute" :class="[{'under-overlay' : overlay}, {'thread' : productId == 'button-holes'}]"></div>
                <SelectedFabric>
                    <h5 class="heading">{{ product.name }}</h5>
                </SelectedFabric>
            </template>

            <!-- Selected product is shown next to or under selected fabric -->
            <template v-else-if="isBoth">
                <div class="selected-product product-border">
                    <img v-if="overlay" v-lazy="overlay" class="overlay">
                    <img v-if="product.detailedImage || product.mainImage"
                         v-lazy="product.detailedImage || product.mainImage"
                         :alt="product.name"
                         :title="product.name"
                         :class="[{'under-overlay' : overlay}, {'thread' : productId == 'button-holes'}]"
                    >
                    <div v-else class="image-substitute" :class="[{'under-overlay' : overlay}, {'thread' : productId == 'button-holes'}]"></div>
                    <template v-if="type === 'both-side-second'">
                        <h5 class="heading heading-smaller">{{ $tr.product.secondary.preview }}</h5>
                        <h5 class="heading" v-if="primaryProduct">{{ primaryProduct.name }} </h5>
                    </template>
                    <h5 v-else class="heading">{{ product.name }}</h5>
                </div>
                <SelectedFabric>
                    <!-- Monogram is selected -> show styled monogram text -->
                    <template v-if="category.id === 'monogram' && product.id !== 'none'">
						<span v-if="$db.products['monogram-font'] && $db.products['monogram-text'] && $db.products['monogram-font-style']"
                              class="fabric-text"
                              :style="$db.products['monogram-font-style'] + ';' + $db.products['monogram-color-style']"
                        >
							{{ $db.products["monogram-text"] }}
						</span>
                    </template>
                    <template v-else-if="product.preview">
                        <img :src="product.preview" class="preview-over" :alt="$tr.product.preview + ' ' + category.name">
                    </template>
                </SelectedFabric>

            </template>

            <!-- Simple product sidebar without fabric-->
            <template v-else>

                <template v-if="product.photos">
                    <slider
                        animation="fade"
                        :autoplay="false"
                        :indicators="false"
                        id="productSlider"
                        :height="product.renderedShirt ? (this.viewport > 1200 ? '455px' : '365px') : '300px'"
                    >
                        <slider-item
                            :alt="product.sidebarName || product.name"
                            :title="product.sidebarName || product.name"
                            v-if="product.renderedShirt && $backendConfig.settings.sex === 'male'"
                        >
                            <img v-lazy="product.detailedImage || product.mainImage"
                                 class="next-to-rendered-shirt"
                                 :alt="product.sidebarName || product.name"
                                 :title="product.sidebarName || product.name"
                                 @click="showPopupModal(product.detailedImage || product.mainImage, true, 2)"
                            >
                            <img v-if="product.renderedShirt"
                                 v-lazy="product.renderedShirt"
                                 class="rendered-shirt"
                                 :alt="$tr.product.renderPreview"
                                 :title="$tr.product.renderPreview"
                                 @click="showPopupModal(product.renderedShirt, true, 0)"
                            >
                        </slider-item>
                        <slider-item
                            :alt="product.sidebarName || product.name"
                            :title="product.sidebarName || product.name"
                        >
                            <img v-lazy="product.sidebarImage || product.mainImage"
                                 :class="[ productId === 'collar' ? 'fit-contain-auto' : '' ]"
                                 :alt="product.sidebarName || product.name"
                                 :title="product.sidebarName || product.name"
                                 @click="showPopupModal(product.sidebarImage || product.mainImage, true, 1)"
                            >
                        </slider-item>
                        <slider-item
                            v-for="(image, index) in product.photos"
                            :key="index"
                            :alt="product.sidebarName || product.name"
                            :title="product.sidebarName || product.name"
                        >
                            <img v-lazy="image"
                                 :class="productId === 'collar' ? 'fit-contain-auto' : ''"
                                 @click="showPopupModal(image, true, index + 2)"
                            >
                        </slider-item>
                    </slider>
                </template>
                <template v-else>
                    <img v-lazy="product.sidebarImage || product.mainImage"
                         :class="[ productId === 'collar' ? 'fit-contain-auto' : '', product.renderedShirt ? 'next-to-rendered-shirt' : '' ]"
                         :alt="product.sidebarName || product.name"
                         :title="product.sidebarName || product.name"
                    >
                    <img v-if="product.renderedShirt"
                         v-lazy="product.renderedShirt"
                         class="rendered-shirt"
                         :alt="$tr.product.renderPreview"
                         :title="$tr.product.renderPreview"
                    >
                </template>

                <!-- Product details -->
                <h5 class="heading">{{ product.sidebarName || product.name }}</h5>
                <p>{{ product.description }}</p>
                <div class="product-specs">
                    <div class="detail" v-for="(property, index) in product.visibleProperties" :key="index">
                        <span>{{ property.name }}:</span>
                        <span>{{ property.value }}</span>
                    </div>
                </div>
            </template>
        </template>

        <Modal v-model="showPopup" class="slider-modal">
            <template v-if="!showSliderInModal">
                <img :src="popupSrc" alt="">
            </template>
            <template v-else>
                <slider
                    animation="fade"
                    :autoplay="false"
                    :indicators="false"
                    id="productModalSlider"
                    v-model="productModalSlider"
                    height="600px"
                >
                    <slider-item
                        :alt="product.sidebarName || product.name"
                        :title="product.sidebarName || product.name"
                        v-if="product.renderedShirt && $backendConfig.settings.sex === 'male'"
                    >
                        <img v-if="product.renderedShirt"
                             v-lazy="product.renderedShirt"
                             class="rendered-shirt"
                             :alt="$tr.product.renderPreview"
                             :title="$tr.product.renderPreview"
                             key="0"
                        >
                    </slider-item>
                    <slider-item
                        :alt="product.sidebarName || product.name"
                        :title="product.sidebarName || product.name"
                    >
                        <img v-lazy="product.sidebarImage || product.mainImage"
                             :class="[ productId === 'collar' ? 'fit-contain-auto' : '' ]"
                             :alt="product.sidebarName || product.name"
                             :title="product.sidebarName || product.name"
                             key="1"
                        >
                    </slider-item>
                    <slider-item
                        v-for="(image, index) in product.photos"
                        :key="index + 2"
                        :alt="product.sidebarName || product.name"
                        :title="product.sidebarName || product.name"
                    >
                        <img v-lazy="image"
                             :class="productId === 'collar' ? 'fit-contain-auto' : ''"
                        >
                    </slider-item>
                </slider>
            </template>
        </Modal>

    </div>
</template>

<script>
import SelectedFabric from "../common/SelectedFabric";
import { Slider, SliderItem } from 'vue-easy-slider'
import Modal from "../common/Modal";

export default {
    name: "ProductSidebar",
    components: {
        SelectedFabric,
        Slider,
        SliderItem,
        Modal
    },
    props: [
        "type",
        "product",
        "category",
        "open",
        "overlay",
        "productId",
    ],
    data() {
        return {
            showPopup: false,
            popupSrc: '',
            showSliderInModal: false,
            sliderIndex: 1,
            viewport: this.$grid.viewportWidth()
        }
    },
    computed: {
        // Sidebar type is both-under, both-over or both-side
        isBoth() {
            return this.type.substr(0, 4) === "both";
        },

        primaryProduct() {
            return this.$db.products[this.category.id];
        }
    },
    methods: {
        showPopupModal(src, slider = false, sliderIndex = 1) {
            this.showSliderInModal = slider;
            this.productModalSlider = sliderIndex;
            if(sliderIndex !== 1)
                this.sliderIndex = sliderIndex
            if(Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) > 991
                && this.productId !== 'button-holes'){
                this.showPopup = true;
                this.popupSrc = src;
            }
        }
    },
    created() {
        window.addEventListener("resize", () => {
            this.viewport = this.$grid.viewportWidth();
        });
    }
}
</script>
